/**
 * Shared, copy-paste-ready code snippets for the Developer Help recipes.
 * All builders take the effective API base URL so examples match the
 * environment the user is looking at.
 */

import type { RecipeCodeSample } from './types';

const SLUG = 'my-agent';

export const createAgentCurl = (baseUrl: string): RecipeCodeSample => ({
  title: 'Create an agent via REST',
  languages: {
    curl: `curl -X POST ${baseUrl}/api/agents \\
  -H "Authorization: Bearer $JWT" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Claims Helper",
    "slug": "claims-helper",
    "description": "Answers questions about claim status and coverage.",
    "systemPrompt": "You are Claims Helper, a precise assistant for claims agents. Answer only from provided data. If unsure, say so."
  }'`,
  },
});

export const addFunctionCurl = (baseUrl: string): RecipeCodeSample => ({
  title: 'Attach a custom function',
  languages: {
    curl: `curl -X POST ${baseUrl}/api/agents/$AGENT_ID/functions \\
  -H "Authorization: Bearer $JWT" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "get_claim_status",
    "description": "Returns the current status of a claim by its claim number.",
    "parametersJson": "{\\"type\\":\\"object\\",\\"properties\\":{\\"claimNumber\\":{\\"type\\":\\"string\\"}},\\"required\\":[\\"claimNumber\\"]}"
  }'`,
  },
});

export const assignSkillCurl = (baseUrl: string): RecipeCodeSample => ({
  title: 'Assign a skill to the agent',
  languages: {
    curl: `curl -X POST ${baseUrl}/api/agents/$AGENT_ID/skills \\
  -H "Authorization: Bearer $JWT" \\
  -H "Content-Type: application/json" \\
  -d '{ "skillId": "SKILL_GUID" }'`,
  },
});

export const chatCompletionSample = (baseUrl: string): RecipeCodeSample => ({
  title: 'Call your agent (single turn)',
  languages: {
    curl: `curl -X POST ${baseUrl}/v1/agents/${SLUG}/chat/completions \\
  -H "Api-Key: $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [{"role": "user", "content": "What can you help me with?"}],
    "stream": false
  }'`,
    python: `from openai import OpenAI

client = OpenAI(
    api_key="YOUR_API_KEY",
    base_url="${baseUrl}/v1/agents/${SLUG}",
)

response = client.chat.completions.create(
    model="cortex",
    messages=[{"role": "user", "content": "What can you help me with?"}],
)
print(response.choices[0].message.content)`,
    csharp: `// dotnet add package AgentOps.Client
using AgentOps.Client;
using AgentOps.Client.Models;

var client = new AgentOpsClient("YOUR_API_KEY", new AgentOpsClientOptions
{
    BaseUrl = "${baseUrl}"
});

var response = await client.Chat.CreateCompletionAsync("${SLUG}", new ChatCompletionRequest
{
    Messages = [new ChatMessage("user", "What can you help me with?")]
});
Console.WriteLine(response.Choices[0].Message?.Content);`,
  },
});

export const multiTurnSample = (baseUrl: string): RecipeCodeSample => ({
  title: 'Multi-turn with session_id (server keeps the history)',
  languages: {
    curl: `# Turn 2 — pass the session_id returned by turn 1
curl -X POST ${baseUrl}/v1/agents/${SLUG}/chat/completions \\
  -H "Api-Key: $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [{"role": "user", "content": "What did I just say?"}],
    "session_id": "SESSION_GUID_FROM_PREVIOUS_RESPONSE",
    "stream": false
  }'`,
    python: `# The openai SDK does not expose session_id directly; pass it as an extra body field.
response = client.chat.completions.create(
    model="cortex",
    messages=[{"role": "user", "content": "What did I just say?"}],
    extra_body={"session_id": "SESSION_GUID_FROM_PREVIOUS_RESPONSE"},
)
print(response.choices[0].message.content)`,
    csharp: `// Turn 1
var turn1 = await client.Chat.CreateCompletionAsync("${SLUG}", new ChatCompletionRequest
{
    Messages = [new ChatMessage("user", "My name is Alice.")]
});

// Turn 2 — server remembers the conversation via SessionId
var turn2 = await client.Chat.CreateCompletionAsync("${SLUG}", new ChatCompletionRequest
{
    Messages = [new ChatMessage("user", "What is my name?")],
    SessionId = turn1.SessionId
});
Console.WriteLine(turn2.Choices[0].Message?.Content); // "Alice"`,
  },
});

export const streamingSample = (baseUrl: string): RecipeCodeSample => ({
  title: 'Streaming',
  languages: {
    curl: `curl -X POST ${baseUrl}/v1/agents/${SLUG}/chat/completions \\
  -H "Api-Key: $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [{"role": "user", "content": "Count to 5"}],
    "stream": true
  }'`,
    python: `stream = client.chat.completions.create(
    model="cortex",
    messages=[{"role": "user", "content": "Count to 5"}],
    stream=True,
)
for chunk in stream:
    if chunk.choices[0].delta.content:
        print(chunk.choices[0].delta.content, end="")`,
    csharp: `await foreach (var chunk in client.Chat.CreateCompletionStreamAsync("${SLUG}", new ChatCompletionRequest
{
    Messages = [new ChatMessage("user", "Count to 5")],
    Stream = true
}))
{
    Console.Write(chunk.Choices.FirstOrDefault()?.Delta?.Content);
}`,
  },
});
